import type {
  DashboardDTO,
  DashboardFilterDTO,
  DashboardInsertDTO,
  DashboardPanelDTO,
  DashboardPanelUpdateDTO,
  DashboardUpdateDTO,
  FilterUpsertDTO,
} from '@tempo-io/roadmap-dto';
import { dashboardPanelDTOSchema, parseDashboardDTO } from '@tempo-io/roadmap-dto';
import { HTTPError } from 'ky';
import { tempoApi } from '../tempoApi';

type RawDashboardFilterDTO = DashboardFilterDTO;

const fetchDashboards = async (loopId: number): Promise<DashboardDTO[]> => {
  const response = await tempoApi().get(`api/v1/loop-visualization/${loopId}/dashboard`);
  const data = await response.json<unknown[]>();
  return data.map(parseDashboardDTO);
};

const fetchDashboard = async (dashboardId: number): Promise<DashboardDTO> => {
  const response = await tempoApi().get(`api/v1/loop-visualization/dashboard/${dashboardId}`);
  return parseDashboardDTO(await response.json());
};

const createDashboard = async (data: DashboardInsertDTO): Promise<DashboardDTO> => {
  const response = await tempoApi().put('api/v1/loop-visualization/dashboard', { json: data });
  return parseDashboardDTO(await response.json());
};

const updateDashboard = async (
  dashboardId: number,
  data: DashboardUpdateDTO,
): Promise<DashboardDTO> => {
  const response = await tempoApi().put(`api/v1/loop-visualization/dashboard/${dashboardId}`, {
    json: data,
  });
  return parseDashboardDTO(await response.json());
};

const updateDashboardPanel = async (
  dashboardId: number,
  position: number,
  data: DashboardPanelUpdateDTO,
): Promise<DashboardPanelDTO> => {
  const response = await tempoApi().put(
    `api/v1/loop-visualization/dashboard/${dashboardId}/panel/${position}`,
    { json: data },
  );
  return dashboardPanelDTOSchema.parse(await response.json());
};

const fetchDashboardFilters = async (dashboardId: number): Promise<DashboardFilterDTO[]> => {
  const response = await tempoApi().get(
    `api/v1/loop-visualization/dashboard/${dashboardId}/filter`,
  );
  return response.json<RawDashboardFilterDTO[]>();
};

const fetchDashboardFilter = async (
  dashboardId: number,
  fieldId: string,
): Promise<DashboardFilterDTO | null> => {
  try {
    const response = await tempoApi().get(
      `api/v1/loop-visualization/dashboard/${dashboardId}/filter/${fieldId}`,
    );
    return await response.json<RawDashboardFilterDTO>();
  } catch (err: unknown) {
    if (err instanceof HTTPError && err.response.status === 404) {
      return null;
    }
    throw err;
  }
};

const upsertDashboardFilter = async (
  dashboardId: number,
  fieldId: string,
  data: FilterUpsertDTO,
): Promise<void> => {
  await tempoApi().put(`api/v1/loop-visualization/dashboard/${dashboardId}/filter/${fieldId}`, {
    json: data,
  });
};

const deleteDashboardFilter = async (dashboardId: number, fieldId: string): Promise<void> => {
  await tempoApi().delete(`api/v1/loop-visualization/dashboard/${dashboardId}/filter/${fieldId}`);
};

export const DashboardApi = {
  fetchDashboards,
  fetchDashboard,
  createDashboard,
  updateDashboard,
  updateDashboardPanel,
  fetchDashboardFilters,
  fetchDashboardFilter,
  upsertDashboardFilter,
  deleteDashboardFilter,
} as const;
